// react
// dependencies
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";

// components
import Row from "./components/Row";
import TableEmpty from "./components/TableEmpty";
import TableHead from "./components/TableHead";
import TableLoading from "./components/TableLoading";

// hooks
import useWindowSize from "../../hooks/useWindowSize";

// utils
import get from "../../utils/GetNested";

// style
import css from "./Table.module.scss";

/**
 * @deprecated Use the Table component from the py-components package instead
 */
const Table = (props) => {
	const {
		rows = [],
		columns = [],
		subheaders = {},
		activeSort = null,
		activeSortDirection = null,
		onColumnClick = () => {},
		highlightColumn = null,
		bodyCellClass = null,
		headCellClass = null,
		hideHeader = false,
		isLoading = false,
		rowKey = "id",
		theme = "vJan20",
		emptyText = null,
		emptyChildren = null,
		tableBodyClass = css.tableBody,
		tableRowClass = null,
	} = props;

	const tableColumnRefs = useRef([]);

	useEffect(() => {
		tableColumnRefs.current = tableColumnRefs.current.slice(0, columns.length);
	}, [JSON.stringify(columns.map((col) => ({ ...col, label: null })))]);

	let columnRefWidths = [];

	if (tableColumnRefs.current.length) {
		// get width of columns
		columnRefWidths = tableColumnRefs.current.map((column) => column.offsetWidth);
	}

	const [highlightPosition, setHighlightPosition] = useState(null);

	const [width] = useWindowSize();
	const [colWidths, subheaderColWidths] = useMemo(() => {
		// set column widths based on screen size
		const currentColWidths = columns
			.map((col) => {
				if (col.mobileViewHideColumn < width || !col.mobileViewHideColumn) {
					return get(col, ["width"], "1fr");
				}
				return "";
			})
			.join(" ");

		// set subheader column widths based on screen size
		const subheaderValues = Object.values(subheaders);

		const subheaderItems = [];
		subheaderValues.forEach((group) => {
			group.forEach((subheader) => {
				if (subheader.mobileViewHideColumn < width || !subheader.mobileViewHideColumn) {
					const item = get(subheader, ["width"], "1fr");
					subheaderItems.push(item);
				}
			});
		});

		const subheaderWidths = subheaderItems.join(" ");

		return [currentColWidths, subheaderWidths];
	}, [width, columns]);

	const handleColumnClick = (columnID, nextDirection) => {
		onColumnClick(columnID, nextDirection);
	};

	const handleUpdateHighlight = () => {
		const columnToHighlightIndex = columns.findIndex((column) => column.id === highlightColumn);
		const columnToHighlightRef = tableColumnRefs.current[columnToHighlightIndex];

		if (!columnToHighlightRef) {
			setHighlightPosition(null);
			return;
		}

		const leftRelativeToParent = columnToHighlightRef.offsetParent.offsetLeft;
		const position = columnToHighlightRef.getBoundingClientRect();

		// update position of column highlighter
		setHighlightPosition({
			width: position.width,
			left: leftRelativeToParent,
		});
	};

	return (
		<div className={`${css.main} ${css[`theme-${theme}`]}`}>
			{highlightColumn && highlightPosition ? (
				<div
					className={css.highlightColumn}
					style={{
						left: highlightPosition.left,
						width: highlightPosition.width,
					}}
				/>
			) : null}

			{!hideHeader && (
				<TableHead
					tableColumnRefs={tableColumnRefs}
					columns={columns}
					colWidths={colWidths}
					subheaders={subheaders}
					activeSort={activeSort}
					highlightColumn={highlightColumn}
					onColumnClick={handleColumnClick}
					defaultCellClassName={headCellClass}
					onUpdateHighlight={handleUpdateHighlight}
					activeSortDirection={activeSortDirection}
					currentScreenWidth={width}
				/>
			)}

			{isLoading ? (
				<>
					<TableLoading
						columnWidths={colWidths}
						cols={columns.length}
						rows={rows.length > 0 ? rows.length : 20}
					/>
				</>
			) : rows.length === 0 && (emptyText || emptyChildren) ? (
				<TableEmpty emptyText={emptyText} emptyChildren={emptyChildren} />
			) : (
				<>
					{rows.length === 0 ? null : (
						<div data-testid="tableLiteBody" className={tableBodyClass}>
							{rows.map((row) => (
								<Row
									columnRefWidths={columnRefWidths}
									tableColumnRefs={tableColumnRefs}
									colWidths={colWidths}
									subheaderColWidths={subheaderColWidths}
									key={get(row, rowKey)}
									row={row}
									columns={columns}
									subheaders={subheaders}
									cellClassName={bodyCellClass}
									currentScreenWidth={width}
									hoverRowURL={get(row, "hoverRowURL", "")}
									rowClassName={tableRowClass}
								/>
							))}
						</div>
					)}
				</>
			)}
		</div>
	);
};

Table.propTypes = {
	isLoading: PropTypes.bool,
	onColumnClick: PropTypes.func,
	activeSort: PropTypes.string,
	activeSortDirection: PropTypes.string,
	highlightColumn: PropTypes.string,
	bodyCellClass: PropTypes.string,
	headCellClass: PropTypes.string,
	subheaders: PropTypes.object,
	rows: PropTypes.array.isRequired,
	columns: PropTypes.array.isRequired,
	rowKey: PropTypes.string,
	theme: PropTypes.oneOf(["vJan20", "vJan23", "vJul23"]),
	emptyText: PropTypes.node,
	emptyChildren: PropTypes.node,
	hideHeader: PropTypes.bool,
	tableBodyClass: PropTypes.string,
	tableRowClass: PropTypes.string,
};

export default Table;
